import { useState } from 'react';
import { User, Tenant, BillingPlan } from '../types/user';
import { TopHeader } from './TopHeader';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { aiProviders } from '../data/ai-providers';
import { useToast } from '../hooks/use-toast';
import { UsersManagement } from './UsersManagement';

interface CompanySettingsProps {
  user: User;
  tenant: Tenant;
  onLogout: () => void;
  onBackToChat: () => void;
  onTenantUpdate?: (updatedTenant: Tenant) => void;
}

type SettingsView = 'users' | 'ai-config' | 'security' | 'content-restrictions' | 'billing' | 'settings';

export function CompanySettings({ user, tenant, onLogout, onBackToChat, onTenantUpdate }: CompanySettingsProps) {
  const [activeView, setActiveView] = useState<SettingsView>('users');
  
  const toast = useToast();
  
  // Estados para configurações de IA
  const [enabledAIs, setEnabledAIs] = useState<string[]>(tenant.settings?.enabledAIs || ['openai', 'anthropic', 'google']);
  
  // Estados para configurações de segurança
  const [securitySettings, setSecuritySettings] = useState(tenant.settings?.security || {});
  const [newAllowedIP, setNewAllowedIP] = useState('');
  
  // Estados para restrições de conteúdo
  const [contentSettings, setContentSettings] = useState(tenant.settings?.contentRestrictions || {});
  const [newRestrictedTopic, setNewRestrictedTopic] = useState('');

  // Estados para dados da conta
  const [accountData, setAccountData] = useState(tenant.contact);

  // Planos disponíveis
  const availablePlans: BillingPlan[] = [
    {
      id: 'free',
      name: 'Free',
      pricePerUser: 0,
      maxUsers: 5,
      monthlyMessageLimit: 1000,
      features: ['Chat básico', 'GPT-3.5', 'Claude Instant'],
      description: 'Ideal para testes e pequenas equipes'
    },
    {
      id: 'pro',
      name: 'Pro',
      pricePerUser: 29.90,
      maxUsers: 50,
      monthlyMessageLimit: 10000,
      features: ['Todos os modelos de IA', 'Configurações avançadas', 'Suporte prioritário', 'Relatórios'],
      description: 'Perfeito para empresas em crescimento'
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      pricePerUser: 99.90,
      maxUsers: 500,
      monthlyMessageLimit: 100000,
      features: ['Tudo do Pro', 'SLA garantido', 'Integração personalizada', 'Manager dedicado'],
      description: 'Para grandes organizações'
    }
  ];

  const companyUsers = [
    { id: '1', name: 'João Silva', email: 'joao@demo.com', role: 'user', status: 'active', lastLogin: new Date() },
    { id: '2', name: 'Maria Santos', email: 'maria@demo.com', role: 'user', status: 'active', lastLogin: new Date('2024-01-20') },
    { id: '3', name: 'Pedro Costa', email: 'pedro@demo.com', role: 'user', status: 'active', lastLogin: new Date('2024-01-19') },
    { id: '4', name: 'Ana Oliveira', email: 'ana@demo.com', role: 'manager', status: 'active', lastLogin: new Date() }
  ];

  // Filtrar itens do menu baseado no tipo de conta
  const allSidebarItems = [
    { id: 'users', label: 'Usuários', icon: '👥', requiresEnterprise: false },
    { id: 'ai-config', label: 'Config. IA', icon: '🤖', requiresEnterprise: true },
    { id: 'security', label: 'Segurança', icon: '🔒', requiresEnterprise: true },
    { id: 'content-restrictions', label: 'Restrição de Temas', icon: '🚫', requiresEnterprise: true },
    // { id: 'billing', label: 'Faturamento', icon: '💳', requiresEnterprise: false }, // REMOVIDO
    { id: 'settings', label: 'Configurações', icon: '⚙️', requiresEnterprise: false }
  ];

  // Mostrar apenas itens permitidos para o tipo de conta
  const sidebarItems = allSidebarItems.filter(item => {
    // Se não requer Enterprise, mostrar para todos
    if (!item.requiresEnterprise) return true;
    // Se requer Enterprise, mostrar apenas para contas Enterprise
    return tenant.accountType === 'enterprise';
  });

  // Funções para configurações de IA
  const toggleAI = (aiId: string) => {
    setEnabledAIs(prev => {
      if (prev.includes(aiId)) {
        return prev.filter(id => id !== aiId);
      } else {
        return [...prev, aiId];
      }
    });
  };

  const saveAIConfig = async () => {
    try {
      // Atualizar o tenant com as novas configurações
      const updatedTenant = {
        ...tenant,
        settings: {
          ...tenant.settings,
          enabledAIs: enabledAIs
        }
      };
      
      // Notificar o componente pai sobre a atualização (que salvará no Supabase)
      if (onTenantUpdate) {
        await onTenantUpdate(updatedTenant);
      }
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast.error('Erro ao salvar configurações. Tente novamente.');
    }
  };

  // Funções para configurações de segurança
  const handleSecuritySettingChange = (key: keyof typeof securitySettings, value: any) => {
    setSecuritySettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const addAllowedIP = () => {
    if (newAllowedIP && !securitySettings.allowedIPs.includes(newAllowedIP)) {
      const ipPattern = /^(\d{1,3}\.){3}\d{1,3}$/;
      if (ipPattern.test(newAllowedIP) || newAllowedIP.includes('*')) {
        setSecuritySettings(prev => ({
          ...prev,
          allowedIPs: [...prev.allowedIPs, newAllowedIP]
        }));
        setNewAllowedIP('');
        toast.success('IP adicionado com sucesso!');
      } else {
        toast.error('Formato de IP inválido. Use formato como 192.168.1.1 ou 192.168.*');
      }
    }
  };

  const removeAllowedIP = (ip: string) => {
    setSecuritySettings(prev => ({
      ...prev,
      allowedIPs: prev.allowedIPs.filter(allowedIP => allowedIP !== ip)
    }));
    toast.success('IP removido com sucesso!');
  };

  // Funções para restrições de conteúdo
  const handleContentSettingChange = (key: keyof typeof contentSettings, value: any) => {
    setContentSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const addRestrictedTopic = () => {
    if (newRestrictedTopic && !contentSettings.restrictedTopics.includes(newRestrictedTopic)) {
      setContentSettings(prev => ({
        ...prev,
        restrictedTopics: [...prev.restrictedTopics, newRestrictedTopic]
      }));
      setNewRestrictedTopic('');
      toast.success('Tema restrito adicionado com sucesso!');
    }
  };

  const removeRestrictedTopic = (topic: string) => {
    setContentSettings(prev => ({
      ...prev,
      restrictedTopics: prev.restrictedTopics.filter(t => t !== topic)
    }));
    toast.success('Tema removido das restrições!');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Simular upload de arquivo
      const newFile = {
        id: `file-${Date.now()}`,
        name: file.name,
        uploadedAt: new Date(),
        size: file.size,
        type: file.type
      };
      
      setContentSettings(prev => ({
        ...prev,
        policyFiles: [...prev.policyFiles, newFile]
      }));
      
      toast.success(`Arquivo "${file.name}" carregado com sucesso!`);
      event.target.value = '';
    }
  };

  const removePolicyFile = (fileId: string) => {
    setContentSettings(prev => ({
      ...prev,
      policyFiles: prev.policyFiles.filter(f => f.id !== fileId)
    }));
    toast.success('Arquivo de política removido!');
  };

  const saveSecuritySettings = () => {
    // Simular salvamento
    toast.success('Configurações de segurança salvas com sucesso!');
  };

  const saveContentSettings = () => {
    // Simular salvamento
    toast.success('Configurações de restrição de conteúdo salvas com sucesso!');
  };

  const saveAccountData = () => {
    // Simular salvamento
    toast.success('Dados da conta atualizados com sucesso!');
  };

  const renderUsers = () => (
    <UsersManagement tenantId={tenant.id} currentUser={user} />
  );

  const renderAIConfig = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">IAs Disponíveis</h2>
        <Button onClick={saveAIConfig}>
          Salvar Alterações
        </Button>
      </div>

      {/* Preview das mudanças */}
      {enabledAIs.length !== (tenant.settings?.enabledAIs?.length || 0) && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <span className="text-xl">💡</span>
            <div>
              <p className="text-sm font-medium text-blue-900">Alterações pendentes</p>
              <p className="text-sm text-blue-700 mt-1">
                {enabledAIs.length > (tenant.settings?.enabledAIs?.length || 0)
                  ? `+${enabledAIs.length - (tenant.settings?.enabledAIs?.length || 0)} IA(s) será(ão) habilitada(s)`
                  : `${(tenant.settings?.enabledAIs?.length || 0) - enabledAIs.length} IA(s) será(ão) desabilitada(s)`
                }. 
                Clique em "Salvar Alterações" para aplicar.
              </p>
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-xl">🤖</span>
            Inteligências Artificiais Disponíveis
          </CardTitle>
          <CardDescription>
            Selecione quais IAs os usuários da sua empresa podem acessar
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {aiProviders.map((provider) => (
              <div key={provider.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                      <span className="text-2xl">{provider.icon}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{provider.name}</h3>
                      <p className="text-sm text-gray-600">{provider.description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={enabledAIs.includes(provider.id)}
                    onCheckedChange={() => toggleAI(provider.id)}
                  />
                </div>

                {/* Mostrar modelos se a IA estiver habilitada */}
                {enabledAIs.includes(provider.id) && (
                  <div className="ml-15 pt-3 border-t">
                    <p className="text-sm font-medium text-gray-700 mb-2">Modelos disponíveis:</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {provider.models.map((model) => (
                        <div key={model.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div>
                            <span className="text-sm font-medium">{model.name}</span>
                            {model.description && (
                              <p className="text-xs text-gray-500">{model.description}</p>
                            )}
                          </div>
                          <Badge variant="outline" className="text-xs">
                            Ativo
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-2">
              <span className="text-xl">💡</span>
              <div>
                <p className="text-sm font-medium text-blue-900">Dica</p>
                <p className="text-sm text-blue-700 mt-1">
                  Usuários só terão acesso às IAs que estiverem habilitadas aqui. 
                  Você pode desabilitar temporariamente uma IA sem perder as configurações.
                </p>
              </div>
            </div>
          </div>

          {/* Status atual das IAs */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-start gap-2">
              <span className="text-xl">👥</span>
              <div>
                <p className="text-sm font-medium text-gray-900">Status atual para usuários</p>
                <p className="text-sm text-gray-700 mt-1">
                  {enabledAIs.length} de {aiProviders.length} IAs estão disponíveis para os usuários da empresa.
                </p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {aiProviders.map(provider => (
                    <span
                      key={provider.id}
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        enabledAIs.includes(provider.id)
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {provider.name}
                      {enabledAIs.includes(provider.id) ? ' ✓' : ' ✗'}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderSecurity = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Segurança</h2>
        <Button onClick={saveSecuritySettings} className="bg-blue-600 hover:bg-blue-700">
          Salvar Alterações
        </Button>
      </div>

      {/* Autenticação de Dois Fatores */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <Label className="text-base font-semibold text-gray-900">Autenticação de Dois Fatores (2FA)</Label>
              <p className="text-sm text-gray-500 mt-1">
                Adicione uma camada extra de segurança para todos os usuários
              </p>
            </div>
            <Switch
              checked={securitySettings.twoFactorEnabled}
              onCheckedChange={(checked) => handleSecuritySettingChange('twoFactorEnabled', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Restrição de IP */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <Label className="text-base font-semibold text-gray-900">Restrição de Acesso por IP</Label>
              <p className="text-sm text-gray-500 mt-1">
                Limite o acesso ao sistema apenas a IPs ou redes específicas
              </p>
            </div>
            <Switch
              checked={securitySettings.ipRestrictionEnabled}
              onCheckedChange={(checked) => handleSecuritySettingChange('ipRestrictionEnabled', checked)}
            />
          </div>

          {/* Campos de IP - Só aparecem quando ativado */}
          {securitySettings.ipRestrictionEnabled && (
            <div className="space-y-4 mt-4 p-4 border-2 border-blue-200 rounded-lg bg-blue-50/50">
              {/* Input para adicionar IP */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Adicionar Endereço IP</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Ex: 192.168.1.1 ou 192.168.* (com wildcard)"
                    value={newAllowedIP}
                    onChange={(e) => setNewAllowedIP(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addAllowedIP()}
                    className="font-mono bg-white"
                  />
                  <Button onClick={addAllowedIP} className="whitespace-nowrap">
                    ➕ Adicionar
                  </Button>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  Pressione Enter ou clique em "Adicionar" para incluir o IP
                </p>
              </div>

              {/* Lista de IPs Permitidos */}
              {securitySettings.allowedIPs && securitySettings.allowedIPs.length > 0 ? (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    IPs Permitidos ({securitySettings.allowedIPs.length}):
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                    {securitySettings.allowedIPs.map((ip, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-green-600">✓</span>
                          <span className="font-mono text-sm font-medium">{ip}</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeAllowedIP(ip)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          ✕ Remover
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-6 bg-gray-50 rounded-lg border border-dashed border-gray-300 text-center">
                  <p className="text-sm text-gray-500">
                    Nenhum IP adicionado ainda. Adicione pelo menos um IP para ativar a restrição.
                  </p>
                </div>
              )}

              {/* Avisos e Dicas */}
              <div className="space-y-3">
                {/* Aviso de segurança */}
                <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-start gap-2">
                    <span className="text-xl">⚠️</span>
                    <div>
                      <p className="text-sm font-medium text-yellow-900">Atenção!</p>
                      <p className="text-sm text-yellow-700 mt-1">
                        <strong>Importante:</strong> Adicione seu IP atual antes de salvar para não perder acesso ao sistema.
                        Se você se bloquear, entre em contato com o suporte.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dicas de uso */}
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-start gap-2">
                    <span className="text-xl">💡</span>
                    <div>
                      <p className="text-sm font-medium text-blue-900">Dicas de uso:</p>
                      <ul className="text-sm text-blue-700 mt-1 space-y-1 list-disc list-inside">
                        <li><strong>IP específico:</strong> 192.168.1.100</li>
                        <li><strong>Range com wildcard:</strong> 192.168.* (permite toda a rede 192.168.x.x)</li>
                        <li><strong>Sub-rede:</strong> 10.0.1.* (permite 10.0.1.0 a 10.0.1.255)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Aviso quando desativado */}
          {!securitySettings.ipRestrictionEnabled && (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-600">
                🌍 Restrição de IP está <strong>desativada</strong>. Todos os IPs podem acessar a plataforma.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Gestão de Senhas */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-6">
            {/* Cabeçalho */}
            <div>
              <Label className="text-base font-semibold text-gray-900">Gestão de Senhas</Label>
              <p className="text-sm text-gray-500 mt-1">
                Configure políticas de senha e sessão para os usuários
              </p>
            </div>

            <div className="border-t border-gray-200"></div>

            {/* Senhas Fortes */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label className="text-sm font-medium text-gray-900">Exigir Senhas Fortes</Label>
                <p className="text-xs text-gray-500 mt-1">
                  Senhas devem ter pelo menos 8 caracteres com números e símbolos
                </p>
              </div>
              <Switch
                checked={securitySettings.requireStrongPasswords}
                onCheckedChange={(checked) => handleSecuritySettingChange('requireStrongPasswords', checked)}
              />
            </div>

            <div className="border-t border-gray-200"></div>

            {/* Expiração de Senha */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-900">Expiração de Senha</Label>
              <p className="text-xs text-gray-500">
                Usuários serão obrigados a trocar a senha após este período
              </p>
              <div className="flex items-center gap-2">
                <Input
                  id="password-expiry"
                  type="number"
                  value={securitySettings.passwordExpirationDays}
                  onChange={(e) => handleSecuritySettingChange('passwordExpirationDays', parseInt(e.target.value))}
                  min="30"
                  max="365"
                  className="w-32"
                />
                <span className="text-sm text-gray-600 font-medium">dias</span>
              </div>
            </div>

            <div className="border-t border-gray-200"></div>

            {/* Timeout de Sessão */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-900">Timeout de Sessão</Label>
              <p className="text-xs text-gray-500">
                Usuários serão desconectados automaticamente após este período de inatividade
              </p>
              <div className="flex items-center gap-2">
                <Input
                  id="session-timeout"
                  type="number"
                  value={securitySettings.sessionTimeout}
                  onChange={(e) => handleSecuritySettingChange('sessionTimeout', parseInt(e.target.value))}
                  min="30"
                  max="1440"
                  className="w-32"
                />
                <span className="text-sm text-gray-600 font-medium">minutos</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderContentRestrictions = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Restrição de Temas</h2>
        <Button onClick={saveContentSettings} className="bg-blue-600 hover:bg-blue-700">
          Salvar Alterações
        </Button>
      </div>

      {/* Restrições Padrão */}
      <Card>
        <CardHeader>
          <CardTitle>Restrições Padrão</CardTitle>
          <CardDescription>
            Ative ou desative bloqueios automáticos para temas sensíveis
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Conteúdo Sexual */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🔞</span>
              <div>
                <Label className="text-base font-medium cursor-pointer">Conteúdo Sexual</Label>
                <p className="text-sm text-gray-500 mt-1">
                  Bloqueia conversas com conteúdo sexual explícito ou inapropriado
                </p>
              </div>
            </div>
            <Switch
              checked={contentSettings.restrictedTopics?.includes('conteúdo sexual')}
              onCheckedChange={(checked) => {
                if (checked) {
                  addRestrictedTopic('conteúdo sexual');
                } else {
                  removeRestrictedTopic('conteúdo sexual');
                }
              }}
            />
          </div>

          {/* Racismo */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <Label className="text-base font-medium cursor-pointer">Racismo e Discriminação</Label>
                <p className="text-sm text-gray-500 mt-1">
                  Bloqueia conteúdo racista, discriminatório ou de ódio
                </p>
              </div>
            </div>
            <Switch
              checked={contentSettings.restrictedTopics?.includes('racismo e discriminação')}
              onCheckedChange={(checked) => {
                if (checked) {
                  addRestrictedTopic('racismo e discriminação');
                } else {
                  removeRestrictedTopic('racismo e discriminação');
                }
              }}
            />
          </div>

          {/* Violência */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🔪</span>
              <div>
                <Label className="text-base font-medium cursor-pointer">Violência e Conteúdo Gráfico</Label>
                <p className="text-sm text-gray-500 mt-1">
                  Bloqueia conteúdo violento, gore ou perturbador
                </p>
              </div>
            </div>
            <Switch
              checked={contentSettings.restrictedTopics?.includes('violência')}
              onCheckedChange={(checked) => {
                if (checked) {
                  addRestrictedTopic('violência');
                } else {
                  removeRestrictedTopic('violência');
                }
              }}
            />
          </div>

          {/* Dados Sensíveis */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🔒</span>
              <div>
                <Label className="text-base font-medium cursor-pointer">Dados Sensíveis</Label>
                <p className="text-sm text-gray-500 mt-1">
                  Bloqueia compartilhamento de senhas, CPF, cartões de crédito, etc.
                </p>
              </div>
            </div>
            <Switch
              checked={contentSettings.restrictedTopics?.includes('dados sensíveis')}
              onCheckedChange={(checked) => {
                if (checked) {
                  addRestrictedTopic('dados sensíveis');
                } else {
                  removeRestrictedTopic('dados sensíveis');
                }
              }}
            />
          </div>

          {/* Política */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🏛️</span>
              <div>
                <Label className="text-base font-medium cursor-pointer">Política e Religião</Label>
                <p className="text-sm text-gray-500 mt-1">
                  Bloqueia discussões sobre política partidária e religião
                </p>
              </div>
            </div>
            <Switch
              checked={contentSettings.restrictedTopics?.includes('política e religião')}
              onCheckedChange={(checked) => {
                if (checked) {
                  addRestrictedTopic('política e religião');
                } else {
                  removeRestrictedTopic('política e religião');
                }
              }}
            />
          </div>

          {/* Drogas */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <span className="text-2xl">💊</span>
              <div>
                <Label className="text-base font-medium cursor-pointer">Drogas Ilícitas</Label>
                <p className="text-sm text-gray-500 mt-1">
                  Bloqueia conteúdo relacionado a drogas ilegais
                </p>
              </div>
            </div>
            <Switch
              checked={contentSettings.restrictedTopics?.includes('drogas ilícitas')}
              onCheckedChange={(checked) => {
                if (checked) {
                  addRestrictedTopic('drogas ilícitas');
                } else {
                  removeRestrictedTopic('drogas ilícitas');
                }
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Temas Personalizados */}
      <Card>
        <CardHeader>
          <CardTitle>Temas Personalizados</CardTitle>
          <CardDescription>
            Adicione restrições específicas para sua empresa
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Ex: Concorrentes, Informações financeiras internas..."
              value={newRestrictedTopic}
              onChange={(e) => setNewRestrictedTopic(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addRestrictedTopic()}
            />
            <Button onClick={addRestrictedTopic}>
              Adicionar
            </Button>
          </div>

          {/* Lista de temas personalizados (excluindo os padrões) */}
          {contentSettings.restrictedTopics?.filter(topic => 
            !['conteúdo sexual', 'racismo e discriminação', 'violência', 'dados sensíveis', 'política e religião', 'drogas ilícitas'].includes(topic.toLowerCase())
          ).length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Restrições Personalizadas:</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {contentSettings.restrictedTopics
                  .filter(topic => !['conteúdo sexual', 'racismo e discriminação', 'violência', 'dados sensíveis', 'política e religião', 'drogas ilícitas'].includes(topic.toLowerCase()))
                  .map((topic, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-orange-50 rounded border border-orange-200">
                      <span className="text-sm font-medium text-orange-900">{topic}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeRestrictedTopic(topic)}
                        className="text-orange-700 hover:text-orange-900 hover:bg-orange-100"
                      >
                        Remover
                      </Button>
                    </div>
                  ))}
              </div>
            </div>
          )}

          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-2">
              <span className="text-xl">💡</span>
              <div>
                <p className="text-sm font-medium text-blue-900">Como funciona?</p>
                <p className="text-sm text-blue-700 mt-1">
                  A IA vai recusar responder perguntas relacionadas aos temas restritos. 
                  Use palavras-chave ou frases que identifiquem claramente o tema.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Instruções Personalizadas */}
      <Card>
        <CardHeader>
          <CardTitle>Instruções Personalizadas</CardTitle>
          <CardDescription>
            Descreva com suas próprias palavras quais temas devem ser evitados
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder="Ex: Não discuta informações sobre nossos concorrentes, evite falar sobre processos internos da empresa, não revele dados financeiros..."
            value={contentSettings.customInstructions}
            onChange={(e) => handleContentSettingChange('customInstructions', e.target.value)}
            rows={6}
            className="resize-none"
          />
          <p className="text-xs text-gray-500">
            Estas instruções serão incluídas no contexto da IA para todas as conversas.
          </p>
        </CardContent>
      </Card>
    </div>
  );


  const renderBilling = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Faturamento</h2>
        <Button onClick={onBackToChat} variant="outline">
          ← Voltar ao Chat
        </Button>
      </div>

      {/* Plano Atual */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-xl">💳</span>
            Plano Atual
          </CardTitle>
          <CardDescription>
            Detalhes da sua assinatura ativa
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-gray-600">Plano</Label>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-2xl font-bold">{tenant.plan}</span>
                  <Badge variant="default">Ativo</Badge>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-600">Valor por Usuário</Label>
                <p className="text-xl font-bold mt-1">
                  R$ {tenant.billing?.pricePerUser.toFixed(2)}/mês
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-600">Usuários Ativos</Label>
                <p className="text-lg font-semibold mt-1">
                  {tenant.billing?.activeUsers} usuários
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-600">Total Mensal</Label>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  R$ {((tenant.billing?.pricePerUser || 0) * (tenant.billing?.activeUsers || 0)).toFixed(2)}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-gray-600">Próximo Pagamento</Label>
                <p className="text-lg font-semibold mt-1">
                  {tenant.billing?.nextPayment?.toLocaleDateString('pt-BR')}
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-600">Método de Pagamento</Label>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xl">💳</span>
                  <span>
                    {tenant.billing?.paymentMethod?.brand} •••• {tenant.billing?.paymentMethod?.last4}
                  </span>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-600">Uso de Mensagens</Label>
                <div className="mt-1">
                  <div className="flex justify-between text-sm mb-1">
                    <span>{tenant.billing?.messagesUsed || 0} mensagens</span>
                    <span>{tenant.settings?.monthlyMessageLimit || 'Ilimitado'}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ 
                        width: tenant.settings?.monthlyMessageLimit 
                          ? `${Math.min(((tenant.billing?.messagesUsed || 0) / tenant.settings.monthlyMessageLimit) * 100, 100)}%`
                          : '0%' 
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-4 mt-6 pt-6 border-t">
            <Button variant="outline">
              Alterar Método de Pagamento
            </Button>
            <Button variant="outline">
              Baixar Comprovante
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Alterar Plano */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-xl">🔄</span>
            Alterar Plano
          </CardTitle>
          <CardDescription>
            Escolha o plano que melhor atende suas necessidades
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {availablePlans.map((plan) => (
              <div 
                key={plan.id} 
                className={`border rounded-lg p-6 ${
                  tenant.plan.toLowerCase() === plan.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                }`}
              >
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold">{plan.name}</h3>
                  <p className="text-3xl font-bold mt-2">
                    {plan.pricePerUser === 0 ? 'Grátis' : `R$ ${plan.pricePerUser.toFixed(2)}`}
                  </p>
                  {plan.pricePerUser > 0 && (
                    <p className="text-sm text-gray-500">por usuário/mês</p>
                  )}
                </div>

                <p className="text-sm text-gray-600 mb-4">{plan.description}</p>

                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm">
                      <span className="text-green-500">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button 
                  className="w-full" 
                  variant={tenant.plan.toLowerCase() === plan.id ? "secondary" : "default"}
                  disabled={tenant.plan.toLowerCase() === plan.id}
                >
                  {tenant.plan.toLowerCase() === plan.id ? 'Plano Atual' : 'Alterar para este plano'}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Histórico de Faturas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-xl">📄</span>
            Histórico de Faturas
          </CardTitle>
          <CardDescription>
            Suas faturas dos últimos meses
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {tenant.billing?.invoices.map((invoice) => (
              <div key={invoice.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <span className="text-xl">📄</span>
                  </div>
                  <div>
                    <p className="font-medium">
                      Fatura #{invoice.id.split('_')[1]}
                    </p>
                    <p className="text-sm text-gray-500">
                      {invoice.period.start.toLocaleDateString('pt-BR')} - {invoice.period.end.toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="font-bold">R$ {invoice.amount.toFixed(2)}</p>
                    <p className="text-sm text-gray-500">{invoice.users} usuários</p>
                  </div>
                  <Badge 
                    variant={
                      invoice.status === 'paid' ? 'default' : 
                      invoice.status === 'pending' ? 'secondary' : 'destructive'
                    }
                  >
                    {invoice.status === 'paid' ? 'Pago' : 
                     invoice.status === 'pending' ? 'Pendente' : 'Vencido'}
                  </Badge>
                  <Button variant="outline" size="sm">
                    Download
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Configurações da Conta</h2>
        <Button onClick={saveAccountData}>
          Salvar Alterações
        </Button>
      </div>

      {/* Informações da Empresa */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-xl">🏢</span>
            Informações da Empresa
          </CardTitle>
          <CardDescription>
            Dados principais da sua organização
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company-name">Nome da Empresa</Label>
              <Input
                id="company-name"
                value={accountData.companySize || tenant.name}
                onChange={(e) => setAccountData(prev => ({ ...prev, companySize: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="domain">Domínio</Label>
              <Input
                id="domain"
                value={tenant.domain}
                disabled
                className="bg-gray-50"
              />
              <p className="text-xs text-gray-500">O domínio não pode ser alterado</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tax-id">CNPJ</Label>
              <Input
                id="tax-id"
                value={accountData.taxId}
                onChange={(e) => setAccountData(prev => ({ ...prev, taxId: e.target.value }))}
                placeholder="00.000.000/0000-00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="industry">Setor</Label>
              <Select
                value={accountData.industry}
                onValueChange={(value) => setAccountData(prev => ({ ...prev, industry: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o setor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tecnologia">Tecnologia</SelectItem>
                  <SelectItem value="educacao">Educação</SelectItem>
                  <SelectItem value="saude">Saúde</SelectItem>
                  <SelectItem value="financeiro">Financeiro</SelectItem>
                  <SelectItem value="varejo">Varejo</SelectItem>
                  <SelectItem value="manufactura">Manufatura</SelectItem>
                  <SelectItem value="servicos">Serviços</SelectItem>
                  <SelectItem value="outros">Outros</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company-size">Tamanho da Empresa</Label>
              <Select
                value={accountData.companySize}
                onValueChange={(value) => setAccountData(prev => ({ ...prev, companySize: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tamanho" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1-5">1-5 funcionários</SelectItem>
                  <SelectItem value="6-20">6-20 funcionários</SelectItem>
                  <SelectItem value="21-50">21-50 funcionários</SelectItem>
                  <SelectItem value="51-200">51-200 funcionários</SelectItem>
                  <SelectItem value="201-1000">201-1000 funcionários</SelectItem>
                  <SelectItem value="1000+">1000+ funcionários</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                value={accountData.website}
                onChange={(e) => setAccountData(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://exemplo.com"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Informações de Contato */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-xl">📞</span>
            Informações de Contato
          </CardTitle>
          <CardDescription>
            Dados de contato da empresa
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-mail Principal</Label>
              <Input
                id="email"
                type="email"
                value={accountData.email}
                onChange={(e) => setAccountData(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Telefone</Label>
              <Input
                id="phone"
                value={accountData.phone}
                onChange={(e) => setAccountData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+55 11 99999-0000"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Endereço Completo</Label>
            <Textarea
              id="address"
              value={accountData.address}
              onChange={(e) => setAccountData(prev => ({ ...prev, address: e.target.value }))}
              placeholder="Rua, número, bairro, cidade, estado, CEP"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Configurações da Conta */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-xl">⚙️</span>
            Configurações da Conta
          </CardTitle>
          <CardDescription>
            Configurações gerais da conta
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <div>
              <Label className="text-base font-medium">Status da Conta</Label>
              <div className="mt-2">
                <Badge variant={tenant.status === 'active' ? 'default' : 'destructive'} className="capitalize">
                  {tenant.status === 'active' ? 'Ativa' : tenant.status}
                </Badge>
              </div>
            </div>

            <div>
              <Label className="text-base font-medium">Data de Criação</Label>
              <p className="text-sm text-gray-600 mt-1">
                {tenant.createdAt.toLocaleDateString('pt-BR', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>

            <div>
              <Label className="text-base font-medium">ID da Conta</Label>
              <p className="text-sm font-mono text-gray-600 mt-1">{tenant.id}</p>
            </div>
          </div>

          <div className="pt-4 border-t">
            <div className="space-y-4">
              <Button variant="outline" className="w-full">
                Exportar Dados da Conta
              </Button>
              <Button variant="destructive" className="w-full">
                Excluir Conta
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderView = () => {
    switch (activeView) {
      case 'users':
        return renderUsers();
      case 'ai-config':
        return renderAIConfig();
      case 'security':
        return renderSecurity();
      case 'content-restrictions':
        return renderContentRestrictions();
      // case 'billing': // REMOVIDO
      //   return renderBilling();
      case 'settings':
        return renderSettings();
      default:
        return renderUsers();
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <TopHeader user={user} onLogout={onLogout} />
      
      <div className="flex-1 flex">
        {/* Sidebar */}
        <div className="w-64 bg-white border-r border-gray-200">
          <div className="p-4 border-b">
            <h3 className="font-semibold text-gray-900">{tenant.name}</h3>
            <p className="text-sm text-gray-500">Configurações da Empresa</p>
          </div>
          <nav className="mt-4 px-4">
            <div className="space-y-1">
              {sidebarItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => setActiveView(item.id as SettingsView)}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                    activeView === item.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <span className="mr-3 text-lg">{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
          </nav>
        </div>
        
        {/* Main Content */}
        <main className="flex-1 p-6 overflow-auto">
          {renderView()}
        </main>
      </div>
    </div>
  );
}